// src/context/AuthContext.js
import React, { createContext, useState, useContext, useMemo } from "react";
import { mockFan, mockCreator, mockAdmin } from "../data/mockData";

export const AuthContext = createContext(null);

// Helper function to get the initial user from localStorage
const getInitialUser = () => {
  try {
    const storedUser = localStorage.getItem("currentUser");
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    console.error("Failed to parse user from localStorage", error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(getInitialUser);

  const login = (role = "fan") => {
    let userToLogin;
    let redirectPath;

    if (role === "creator") {
      userToLogin = { ...mockCreator, role: "creator" };
      redirectPath = "/dashboard";
    } else if (role === "admin") {
      userToLogin = { ...mockAdmin, role: "admin" };
      redirectPath = "/admin";
    } else {
      userToLogin = { ...mockFan, role: "fan" };
      redirectPath = "/fan";
    }

    // Save to localStorage
    localStorage.setItem("currentUser", JSON.stringify(userToLogin));
    // Set state
    setCurrentUser(userToLogin);

    return redirectPath;
  };

  const logout = () => {
    // Remove from localStorage
    localStorage.removeItem("currentUser");
    // Set state
    setCurrentUser(null);
  };

  const addConversation = (creator, experienceTitle) => {
    if (!currentUser) return;

    const newConversation = {
      id: `CONV-${Date.now()}`,
      creatorName: creator.fullName,
      creatorProfilePictureUrl: creator.profilePictureUrl,
      lastMessage: `You requested: ${experienceTitle}`,
      lastMessageTimestamp: "Just now",
      isRead: false,
      messages: [
        {
          id: "msg_system_1",
          sender: "system", // A system-generated message
          text: `Your request for "${experienceTitle}" has been sent to ${creator.fullName}. They will contact you here with any questions.`,
          timestamp: "Just now",
        },
      ],
    };

    // Create the new user object with the new conversation
    const updatedUser = {
      ...currentUser,
      conversations: [...(currentUser.conversations || []), newConversation],
    };

    // Update state and localStorage
    setCurrentUser(updatedUser);
    localStorage.setItem("currentUser", JSON.stringify(updatedUser));
  };

  const activateCreator = () => {
    if (!currentUser) return;
    
    // Upgrade user to creator
    const upgradedUser = {
      ...currentUser,
      role: "creator",
      // Add default creator fields if missing
      wallet: currentUser.wallet || mockCreator.wallet,
      stats: currentUser.stats || mockCreator.stats
    };
    
    setCurrentUser(upgradedUser);
    localStorage.setItem("currentUser", JSON.stringify(upgradedUser));
  };

  const registerCreator = (creatorData) => {
    // Create new creator account
    const newCreator = {
      id: `CREATOR-${Date.now()}`,
      role: "creator",
      fullName: creatorData.name,
      email: creatorData.email,
      username: creatorData.name.toLowerCase().replace(/\s+/g, ''),
      category: creatorData.category,
      bio: creatorData.bio || "New creator on Adnaki",
      profilePictureUrl: creatorData.profileImage || "https://api.dicebear.com/7.x/avataaars/svg?seed=" + creatorData.name,
      bannerImageUrl: "https://images.unsplash.com/photo-1557683316-973673baf926?w=1200&h=400&fit=crop",
      isVerified: false,
      joinedDate: new Date().toISOString(),
      
      // Creator-specific fields
      followers: [],
      subscribers: [],
      subscriberCount: 0,
      followerCount: 0,
      
      // Monetization
      products: [],
      experiences: [],
      events: [],
      campaigns: [],
      affiliates: [],
      donations: [],
      
      // Stats
      stats: {
        totalRevenue: 0,
        monthlyRevenue: 0,
        totalViews: 0,
        engagementRate: 0,
        activeSubscribers: 0
      },
      
      // Wallet
      wallet: {
        balance: 0,
        pendingBalance: 0,
        totalEarned: 0,
        transactions: []
      },
      
      // Content
      posts: [],
      media: [],
      
      // Engagement
      fanGroups: [],
      messages: []
    };
    
    setCurrentUser(newCreator);
    localStorage.setItem("currentUser", JSON.stringify(newCreator));
  };

  const value = useMemo(
    () => ({
      currentUser,
      login,
      loginWithCredentials: (username, password) => {
        if (username === "admin" && password === "Adm1n_Adn@k1!2026") {
          return login("admin");
        }
        return null;
      },
      logout,
      addConversation,
      activateCreator,
      registerCreator, // ✨ New creator registration function
    }),
    [currentUser]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
